// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { consoleService } from '../../services/consoleService';
import { User, UserSearchFilters } from '../../types/users';
import { authService } from '../../services/authService';
import { UserRole } from '../../types/auth';
import ConfirmDialog from '../ConfirmDialog';
import './Users.scss';

// Helper function to check if a user is valid
const isValidUser = (user: any): boolean => {
  // Console users have 'username' as identifier, not 'id'
  return user && (user.username || user.id);
};

const Users: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [nextCursor, setNextCursor] = useState('');
  const [prevCursor, setPrevCursor] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newUsername, setNewUsername] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState(4); // Default to Readonly
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  const userRole = authService.getSessionRole();
  const canManageUsers = userRole === UserRole.USER_ROLE_ADMIN;

  // Get filter values from URL params
  const cursor = searchParams.get('cursor') || '';

  useEffect(() => {
    loadUsers();
  }, [searchParams]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');

      const filters: UserSearchFilters = {};
      const data = await consoleService.listUsers(filters, cursor);
      
      console.log('[Users] API Response:', data);
      
      // Get all users from response
      const allUsers = data.users || [];
      console.log('[Users] Total users from API:', allUsers.length, allUsers);
      
      // Filter to only valid users
      const filteredUsers = allUsers.filter(isValidUser);
      console.log('[Users] After filtering:', filteredUsers.length);
      
      setUsers(filteredUsers);
      setTotalCount(data.total_count || filteredUsers.length);
      setNextCursor(data.next_cursor || '');
      setPrevCursor(data.prev_cursor || '');
    } catch (err: any) {
      console.error('[Users] Error loading users:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newCursor: string) => {
    const params = new URLSearchParams(searchParams);
    if (newCursor) {
      params.set('cursor', newCursor);
    } else {
      params.delete('cursor');
    }
    setSearchParams(params);
  };

  const handleCreateUser = async () => {
    if (!newUsername.trim() || !password.trim() || password !== passwordConfirmation) return;

    try {
      setError('');
      await consoleService.createUser({
        username: newUsername.trim(),
        email: newEmail.trim() || undefined,
        password: password.trim(),
        role: newRole,
      });
      
      setShowCreateDialog(false);
      setNewUsername('');
      setNewEmail('');
      setNewRole(4);
      setPassword('');
      setPasswordConfirmation('');
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create user');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setError('');
      await consoleService.deleteUser({
        user_id: selectedUser.username
      });
      
      setShowDeleteDialog(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete user');
    }
  };

  return (
    <div className="users-container">
      {/* Header */}
      <div className="users-header">
        <h2>User Management</h2>
        {canManageUsers && (
          <button className="btn btn-create" onClick={() => setShowCreateDialog(true)}>
            ➕ Create User
          </button>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Users Table */}
      <div className="users-card">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>MFA Required</th>
                    <th>MFA Setup</th>
                    {canManageUsers && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {users.length > 0 ? (
                    users.map((user: any, index) => (
                      <tr key={user.username || index}>
                        <td className="username-cell">
                          <strong>{user.username || '-'}</strong>
                        </td>
                        <td className="email-cell">{user.email || '-'}</td>
                        <td>
                          <span className={`role-badge ${user.role === 1 ? 'admin' : 'user'}`}>
                            {user.role === 1 ? 'Admin' : user.role === 2 ? 'Developer' : user.role === 3 ? 'Maintainer' : user.role === 4 ? 'Readonly' : 'Unknown'}
                          </span>
                        </td>
                        <td>
                          <span className={`mfa-badge ${user.mfa_required ? 'enabled' : 'disabled'}`}>
                            {user.mfa_required ? '✓ Yes' : '✗ No'}
                          </span>
                        </td>
                        <td>
                          <span className={`mfa-badge ${user.mfa_setup ? 'enabled' : 'disabled'}`}>
                            {user.mfa_setup ? '✓ Yes' : '✗ No'}
                          </span>
                        </td>
                        {canManageUsers && (
                          <td>
                            <button
                              className="btn btn-delete"
                              onClick={() => {
                                setSelectedUser(user);
                                setShowDeleteDialog(true);
                              }}
                            >
                              🗑️ Delete
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={canManageUsers ? 6 : 5} className="empty-state">
                        No users found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {(nextCursor || prevCursor) && (
              <div className="pagination">
                <span className="total-count">Total: {totalCount} users</span>
                <div className="pagination-buttons">
                  {prevCursor && (
                    <button className="btn btn-page" onClick={() => handlePageChange('')}>
                      First
                    </button>
                  )}
                  {prevCursor && (
                    <button className="btn btn-page" onClick={() => handlePageChange(prevCursor)}>
                      Previous
                    </button>
                  )}
                  {nextCursor && (
                    <button className="btn btn-page" onClick={() => handlePageChange(nextCursor)}>
                      Next
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create User Dialog */}
      <ConfirmDialog
        isOpen={showCreateDialog}
        title="Create New Console User"
        message={
          <div className="create-user-form-content">
            <div className="form-group">
              <label>Username *</label>
              <input
                type="text"
                className="form-control"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="Enter username"
              />
            </div>
            
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                className="form-control"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter email (optional)"
              />
            </div>

            <div className="form-group">
              <label>Role *</label>
              <select
                className="form-control"
                value={newRole}
                onChange={(e) => setNewRole(Number(e.target.value))}
              >
                <option value={1}>Admin</option>
                <option value={2}>Developer</option>
                <option value={3}>Maintainer</option>
                <option value={4}>Readonly</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Password *</label>
              <input
                type="password"
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
              />
            </div>
            
            <div className="form-group">
              <label>Confirm Password *</label>
              <input
                type="password"
                className={`form-control ${password && passwordConfirmation ? (password === passwordConfirmation ? 'valid' : 'invalid') : ''}`}
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                placeholder="Confirm password"
              />
              {password && passwordConfirmation && password !== passwordConfirmation && (
                <span className="validation-error">Passwords don't match</span>
              )}
            </div>
          </div>
        }
        confirmText="Create User"
        cancelText="Cancel"
        onConfirm={handleCreateUser}
        onCancel={() => {
          setShowCreateDialog(false);
          setNewUsername('');
          setNewEmail('');
          setNewRole(4);
          setPassword('');
          setPasswordConfirmation('');
        }}
        confirmDisabled={!newUsername.trim() || !password.trim() || password !== passwordConfirmation}
      />

      {/* Delete User Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Console User"
        message={
          <div>
            <p>Are you sure you want to delete this console user?</p>
            <p><strong>Username:</strong> {selectedUser?.username}</p>
            <p><strong>Email:</strong> {(selectedUser as any)?.email || '-'}</p>
            <div className="alert alert-warning">
              ⚠️ This action cannot be undone.
            </div>
          </div>
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteUser}
        onCancel={() => {
          setShowDeleteDialog(false);
          setSelectedUser(null);
        }}
        variant="danger"
      />
    </div>
  );
};

export default Users;
